import { forwardRef, createContext, useState, useRef, useMemo, useCallback, useContext } from "react";
import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { createPortal } from "react-dom";
import { DEFAULT_PORTAL_ID } from "./constants/config.js";
import { THEME } from "./constants/theme.js";
import { cn as clsx } from "./helpers/react.js";
import { getRandomID, isFiniteNumber } from "./helpers/utils.js";
import { Button } from "./components/Button/index.js";
import { Icons24 } from "./components/Icons24Sprite/index.js";
import { IconWrapper } from "./components/IconWrapper/index.js";
import { withThemeAndSize } from "./components/withThemeAndSize/index.js";
import { s, a as TOAST_INTENT, c as TOAST_TYPE_CLASSNAME_MAP, b as TOAST_INTENT_CLASSNAME_MAP } from "./constants-mfGraMO4.js";
const Toast = withThemeAndSize(
  forwardRef(
    ({
      message,
      toastKey,
      header,
      type,
      iconName,
      intent = TOAST_INTENT.SECONDARY,
      className,
      theme: _theme,
      size: _size,
      buttons
    }, ref) => {
      const { hideToast } = useToast();
      if (typeof buttons === "undefined") {
        buttons = [
          {
            onClick: () => hideToast(toastKey),
            children: "OK"
          }
        ];
      }
      return /* @__PURE__ */ jsxs(
        "div",
        {
          className: clsx(s.toast, TOAST_INTENT_CLASSNAME_MAP[intent], TOAST_TYPE_CLASSNAME_MAP[type], className),
          ref,
          children: [
            /* @__PURE__ */ jsxs("div", { className: s.container, children: [
              iconName && /* @__PURE__ */ jsx(IconWrapper, { size: "md", className: s.icon, children: /* @__PURE__ */ jsx(Icons24, { name: iconName }) }),
              /* @__PURE__ */ jsxs("div", { className: s.content, children: [
                /* @__PURE__ */ jsx("div", { className: s.header, children: header }),
                /* @__PURE__ */ jsx("div", { className: s.message, children: message })
              ] })
            ] }),
            Array.isArray(buttons) && buttons.length > 0 && /* @__PURE__ */ jsx("div", { className: s.footerContainer, children: buttons.map((button, index) => /* @__PURE__ */ jsx(Button, { ...button }, `toast-button-${index}`)) })
          ]
        }
      );
    }
  )
);
const ToastContext = createContext(null);
const isHorizontalToast = (toastData) => toastData.type === "horizontal";
const ToastProvider = ({ children }) => {
  const [verticalToasts, setVerticalToasts] = useState(/* @__PURE__ */ new Map());
  const [horizontalToasts, setHorizontalToasts] = useState(/* @__PURE__ */ new Map());
  const currentHorizontalToast = useRef(null);
  const timers = useRef(/* @__PURE__ */ new Map());
  const { _dropToast, _setToast, _clearTimers } = useMemo(() => {
    const _dropToast2 = (key) => {
      _dropTimer(key);
      setVerticalToasts((prevToasts) => {
        if (prevToasts.has(key)) {
          const newToasts = new Map(prevToasts);
          newToasts.delete(key);
          return newToasts;
        }
        return prevToasts;
      });
      setHorizontalToasts((prevToasts) => {
        if (prevToasts.has(key)) {
          const newToasts = new Map([...prevToasts]);
          newToasts.delete(key);
          if (currentHorizontalToast.current === key) {
            currentHorizontalToast.current = null;
          }
          const queuedHorizontal = newToasts.values().next().value;
          if (queuedHorizontal && queuedHorizontal.key !== key && queuedHorizontal.key !== currentHorizontalToast.current) {
            currentHorizontalToast.current = queuedHorizontal.key;
            _scheduleHide(queuedHorizontal.key, queuedHorizontal.timeout);
          }
          return newToasts;
        }
        return prevToasts;
      });
    };
    const _scheduleHide = (key, timeout) => {
      if (!isFiniteNumber(timeout) || timeout <= 0) {
        return;
      }
      const timeoutId = self.setTimeout(() => {
        _dropToast2(key);
      }, timeout);
      timers.current.set(key, { timeoutId, state: "running", start: Date.now(), duration: timeout });
    };
    const _setToast2 = (toastData) => {
      if (isHorizontalToast(toastData)) {
        setHorizontalToasts((prevToasts) => {
          const nextToasts = new Map([...prevToasts]);
          if (!nextToasts.has(toastData.key)) {
            nextToasts.set(toastData.key, toastData);
          }
          if (!currentHorizontalToast.current) {
            currentHorizontalToast.current = toastData.key;
            _scheduleHide(toastData.key, toastData.timeout);
          }
          return nextToasts;
        });
        return toastData.key;
      }
      setVerticalToasts((prevToasts) => {
        return new Map([...prevToasts, [toastData.key, toastData]]);
      });
      _scheduleHide(toastData.key, toastData.timeout);
      return toastData.key;
    };
    const _dropTimer = (key) => {
      const timer = timers.current.get(key);
      if (timer) {
        self.clearTimeout(timer.timeoutId);
        timers.current.delete(key);
      }
    };
    const _clearTimers2 = () => {
      for (const [key] of timers.current) {
        _dropTimer(key);
      }
    };
    return { _dropToast: _dropToast2, _setToast: _setToast2, _dropTimer, _clearTimers: _clearTimers2 };
  }, []);
  const hideToast = useCallback((key) => _dropToast(key), [_dropToast]);
  const showToast = useCallback(
    ({ key = getRandomID(), theme = THEME.LIGHT, ...toastData }) => {
      return _setToast({
        ...toastData,
        theme,
        key
      });
    },
    [_setToast]
  );
  const clearToasts = useCallback(() => {
    _clearTimers();
    setVerticalToasts(/* @__PURE__ */ new Map());
    setHorizontalToasts(/* @__PURE__ */ new Map());
    currentHorizontalToast.current = null;
  }, [_clearTimers]);
  const contextValue = useMemo(() => ({ showToast, hideToast, clearToasts }), [clearToasts, hideToast, showToast]);
  const horizontalToast = currentHorizontalToast.current && horizontalToasts.has(currentHorizontalToast.current) ? horizontalToasts.get(currentHorizontalToast.current) : null;
  let portalContainer = document.getElementById(DEFAULT_PORTAL_ID);
  if (!portalContainer) {
    portalContainer = document.body;
  }
  return /* @__PURE__ */ jsxs(ToastContext.Provider, { value: contextValue, children: [
    children,
    portalContainer && createPortal(
      /* @__PURE__ */ jsxs(Fragment, { children: [
        verticalToasts.size > 0 && /* @__PURE__ */ jsx("div", { className: clsx(s.tray, s.vertical), children: Array.from(verticalToasts.values()).reverse().map(({ key, ...data }) => /* @__PURE__ */ jsx(Toast, { toastKey: key, ...data }, key)) }),
        horizontalToast && /* @__PURE__ */ jsx("div", { className: clsx(s.tray, s.horizontal), children: /* @__PURE__ */ jsx(Toast, { toastKey: horizontalToast.key, ...horizontalToast }) })
      ] }),
      portalContainer
    )
  ] });
};
const withToastProvider = (Wrapped) => {
  return (props) => /* @__PURE__ */ jsx(ToastProvider, { children: /* @__PURE__ */ jsx(Wrapped, { ...props }) });
};
const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
export {
  ToastContext as T,
  ToastProvider as a,
  Toast as b,
  useToast as u,
  withToastProvider as w
};
